import { useRouter } from 'next/router';
import { FC } from 'react';

import { useGetProduct } from '@bpm-web-app/swr-hooks';
import { getCurrentPlatformLink } from '@bpm-web-app/utils';
import classNames from 'classnames';
import styles from './product.module.css';
import { Account, AccountPageProps } from '../account';
import { PageHeader } from '../components/page-header/page-header';
import { Button } from '../components/button/button';

type Props = AccountPageProps;

export const ProductPage: FC<Props> = ({ title }) => {
    const router = useRouter();
    // eslint-disable-next-line @typescript-eslint/dot-notation
    const planId = (router.query['sku'] as string);
    const { data } = useGetProduct(planId);
    const pageTitle = data?.product?.name || title;

    if (!data?.product) {
        return (
            <Account title={pageTitle}>
                <PageHeader title={pageTitle} />
            </Account>
        );
    }

    const { product } = data;
    const videoUrl = product.video_url;

    return (
        <Account title={pageTitle} wide>
            <PageHeader title="" />
            <div className={styles['product-container']}>
                <div className={styles['product-video']}>
                    {videoUrl && (
                        <div className={styles['video-wrapper']}>
                            <video
                                controls
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                className={styles['video']}
                                poster={videoUrl.replace('.mp4', '.png')}
                            >
                                <source src={videoUrl} type="video/mp4" />
                                <track kind="captions" />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    )}
                </div>
                <div className={styles['product-details']}>
                    <div className={styles['product-header']}>
                        <div>
                            <h1 className={styles['product-title']}>{product.name}</h1>
                            {product.author && (
                                <p className={styles['product-subtitle']}>
                                    {product.author}
                                </p>
                            )}
                            <div className={styles['product-pricing']}>
                                {product.original_amount && (
                                    <span className={styles['original-price']}>
                                        ${product.original_amount}
                                    </span>
                                )}
                                <span className={styles['current-price']}>
                                    ${product.amount} USD
                                </span>
                            </div>
                        </div>
                        <Button
                            variant="primary"
                            as="button"
                            type="button"
                            disabled={data.owns_product}
                            className={classNames(styles['buy-button'], styles['buy-button--mobile'])}
                            onClick={() => {
                                router.push({
                                    pathname: getCurrentPlatformLink(`/account/products/${product.sku}/buy`),
                                });
                            }}>{data.owns_product ? 'You already own this product' : 'Buy Now'}
                        </Button>
                        {product.description && (
                            <div className={styles['product-description']}>
                                {product.description}
                            </div>
                        )}

                        <table className={styles['product-info-table']}>
                            <tbody>
                                <tr>
                                    <td className={styles['info-label']}>Format</td>
                                    <td className={styles['info-value']}>
                                        PDF (instant download)
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles['info-label']}>Length</td>
                                    <td className={styles['info-value']}>
                                        64 pages
                                    </td>
                                </tr>
                                <tr>
                                    <td className={styles['info-label']}>Author</td>
                                    <td className={styles['info-value']}>
                                        {product.author}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <Button
                        variant="primary"
                        as="button"
                        type="button"
                        disabled={data.owns_product}
                        className={classNames(styles['buy-button'], styles['buy-button--desktop'])}
                        onClick={() => {
                            router.push({
                                pathname: getCurrentPlatformLink(`/account/products/${product.sku}/buy`),
                            });
                        }}>{data.owns_product ? 'You already own this product' : 'Buy Now'}
                    </Button>
                </div>
            </div>
        </Account>
    );
};
